import { GridColDef, GridActionsCellItem, DataGrid } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Alert,
    Snackbar,
    Tooltip,
    FormControlLabel,
    Switch,
    Stack,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
} from "@mui/icons-material";
import {
    procurementService,
    Supplier,
    CreateSupplierRequest,
    UpdateSupplierRequest,
} from "../../services/procurementService";
import { validateAndNormalizeBdPhone } from "../../utils/phone";

const SupplierList: React.FC = () => {
    const theme = useTheme();
    // Phones get cards — the grid needs far more width than a phone has.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeOnly, setActiveOnly] = useState(true);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
        null
    );
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    // Form state for create/edit
    const [formData, setFormData] = useState<CreateSupplierRequest>({
        name: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        payment_terms: "",
    });

    const loadSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await procurementService.getSuppliers({
                search: searchTerm || undefined,
                active_only: activeOnly,
                limit: 1000,
            });
            setSuppliers(data);
        } catch (error) {
            console.error("Error loading suppliers:", error);
            showSnackbar("Failed to load suppliers", "error");
        } finally {
            setLoading(false);
        }
    }, [searchTerm, activeOnly]);

    useEffect(() => {
        loadSuppliers();
    }, [loadSuppliers]);

    const showSnackbar = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCreateSupplier = async () => {
        if (formData.phone?.trim()) {
            const phoneCheck = validateAndNormalizeBdPhone(formData.phone);
            if (!phoneCheck.valid) {
                showSnackbar(phoneCheck.error!, "error");
                return;
            }
        }
        try {
            // Clean empty strings to undefined for optional fields
            const cleanedData = {
                name: formData.name,
                contact_person: formData.contact_person?.trim() || undefined,
                email: formData.email?.trim() || undefined,
                phone: formData.phone?.trim()
                    ? validateAndNormalizeBdPhone(formData.phone).normalized
                    : undefined,
                address: formData.address?.trim() || undefined,
                payment_terms: formData.payment_terms?.trim() || undefined,
            };
            await procurementService.createSupplier(cleanedData);
            showSnackbar("Supplier created successfully", "success");
            setIsCreateDialogOpen(false);
            resetForm();
            loadSuppliers();
        } catch (error) {
            console.error("Error creating supplier:", error);
            showSnackbar("Failed to create supplier", "error");
        }
    };

    const handleUpdateSupplier = async () => {
        if (!selectedSupplier) return;

        if (formData.phone?.trim()) {
            const phoneCheck = validateAndNormalizeBdPhone(formData.phone);
            if (!phoneCheck.valid) {
                showSnackbar(phoneCheck.error!, "error");
                return;
            }
        }
        try {
            // Clean empty strings to undefined for optional fields
            const cleanedData = {
                name: formData.name,
                contact_person: formData.contact_person?.trim() || undefined,
                email: formData.email?.trim() || undefined,
                phone: formData.phone?.trim()
                    ? validateAndNormalizeBdPhone(formData.phone).normalized
                    : undefined,
                address: formData.address?.trim() || undefined,
                payment_terms: formData.payment_terms?.trim() || undefined,
            };
            await procurementService.updateSupplier(
                selectedSupplier.supplier_id,
                cleanedData as UpdateSupplierRequest
            );
            showSnackbar("Supplier updated successfully", "success");
            setIsEditDialogOpen(false);
            resetForm();
            setSelectedSupplier(null);
            loadSuppliers();
        } catch (error) {
            console.error("Error updating supplier:", error);
            showSnackbar("Failed to update supplier", "error");
        }
    };

    const handleDeleteSupplier = async () => {
        if (!selectedSupplier) return;

        try {
            await procurementService.deleteSupplier(
                selectedSupplier.supplier_id
            );
            showSnackbar("Supplier deleted successfully", "success");
            setIsDeleteDialogOpen(false);
            setSelectedSupplier(null);
            loadSuppliers();
        } catch (error) {
            console.error("Error deleting supplier:", error);
            showSnackbar("Failed to delete supplier", "error");
        }
    };

    const handleToggleActive = async (supplier: Supplier) => {
        try {
            await procurementService.updateSupplier(supplier.supplier_id, {
                is_active: !supplier.is_active,
            });
            showSnackbar(
                `Supplier ${
                    supplier.is_active ? "deactivated" : "activated"
                } successfully`,
                "success"
            );
            loadSuppliers();
        } catch (error) {
            console.error("Error toggling supplier status:", error);
            showSnackbar("Failed to update supplier status", "error");
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            contact_person: "",
            email: "",
            phone: "",
            address: "",
            payment_terms: "",
        });
    };

    const openEditDialog = (supplier: Supplier) => {
        setSelectedSupplier(supplier);
        setFormData({
            name: supplier.name,
            contact_person: supplier.contact_person,
            email: supplier.email,
            phone: supplier.phone,
            address: supplier.address,
            payment_terms: supplier.payment_terms || "",
        });
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (supplier: Supplier) => {
        setSelectedSupplier(supplier);
        setIsDeleteDialogOpen(true);
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Supplier Name",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box display="flex" alignItems="center">
                    <BusinessIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {params.row.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {params.row.contact_person}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: "email",
            headerName: "Contact Info",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box>
                    <Box display="flex" alignItems="center" mb={0.5}>
                        <EmailIcon
                            sx={{
                                mr: 1,
                                fontSize: 16,
                                color: "text.secondary",
                            }}
                        />
                        <Typography variant="body2">
                            {params.row.email}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <PhoneIcon
                            sx={{
                                mr: 1,
                                fontSize: 16,
                                color: "text.secondary",
                            }}
                        />
                        <Typography variant="body2">
                            {params.row.phone}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: "address",
            headerName: "Address",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Box display="flex" alignItems="center">
                    <LocationIcon
                        sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" noWrap>
                        {params.row.address}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "payment_terms",
            headerName: "Payment Terms",
            width: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.payment_terms || "Not specified"}
                </Typography>
            ),
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.row.is_active ? "Active" : "Inactive"}
                    color={params.row.is_active ? "success" : "default"}
                    size="small"
                    onClick={() => handleToggleActive(params.row)}
                    clickable
                />
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 120,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={() => openEditDialog(params.row)}
                />,
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() => openDeleteDialog(params.row)}
                />,
            ],
        },
    ];

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                mb={3}
            >
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
                    Suppliers
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadSuppliers}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreateDialogOpen(true)}
                    >
                        {isMobile ? "Add" : "Add Supplier"}
                    </Button>
                </Box>
            </Box>
            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 1, sm: 2 } }}>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                size={isMobile ? "small" : "medium"}
                                placeholder="Search suppliers..."
                                value={searchTerm}
                                onChange={(e) =>
                                    setSearchTerm(e.target.value.toLowerCase())
                                }
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                mr: 1,
                                                color: "text.secondary",
                                            }}
                                        />
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={activeOnly}
                                        onChange={(e) =>
                                            setActiveOnly(e.target.checked)
                                        }
                                    />
                                }
                                label="Show active suppliers only"
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Cards on a phone, grid elsewhere */}
            {isMobile ? (
                loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : suppliers.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
                        No suppliers found.
                    </Typography>
                ) : (
                    suppliers.map((supplier) => (
                        <Card key={supplier.supplier_id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                            <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1 } }}>
                                <Stack spacing={1}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                                        <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
                                            <BusinessIcon sx={{ color: "primary.main", mt: 0.25 }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                                                    {supplier.name}
                                                </Typography>
                                                {supplier.contact_person && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {supplier.contact_person}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {/* Tapping the chip switches active/inactive, as on desktop. */}
                                        <Chip
                                            label={supplier.is_active ? "Active" : "Inactive"}
                                            color={supplier.is_active ? "success" : "default"}
                                            size="small"
                                            onClick={() => handleToggleActive(supplier)}
                                            clickable
                                        />
                                    </Box>
                                    {[
                                        { icon: <PhoneIcon sx={{ fontSize: 16 }} />, text: supplier.phone, href: supplier.phone ? `tel:${supplier.phone}` : undefined },
                                        { icon: <EmailIcon sx={{ fontSize: 16 }} />, text: supplier.email, href: supplier.email ? `mailto:${supplier.email}` : undefined },
                                        { icon: <LocationIcon sx={{ fontSize: 16 }} />, text: supplier.address, href: undefined },
                                    ]
                                        .filter((line) => line.text)
                                        .map((line, i) => (
                                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                                                {line.icon}
                                                <Typography
                                                    variant="body2"
                                                    component={line.href ? "a" : "span"}
                                                    href={line.href}
                                                    sx={{ color: line.href ? "primary.main" : "text.primary", textDecoration: "none", overflowWrap: "anywhere" }}
                                                >
                                                    {line.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                    <Typography variant="caption" color="text.secondary">
                                        Payment terms: {supplier.payment_terms || "Not specified"}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, pt: 0.5 }}>
                                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(supplier)}>
                                            Edit
                                        </Button>
                                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => openDeleteDialog(supplier)}>
                                            Delete
                                        </Button>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))
                )
            ) : (
            <Card>
                <DataGrid
                    rows={suppliers}
                    columns={columns}
                    loading={loading}
                    getRowId={(row) => row.supplier_id}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 50 } },
                    }}
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>
            )}

            {/* Create Dialog */}
            <Dialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add New Supplier</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Supplier Name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Contact Person (Optional)"
                                value={formData.contact_person}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        contact_person: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Email (Optional)"
                                type="email"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        email: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Phone (Optional)"
                                value={formData.phone}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        phone: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Address (Optional)"
                                multiline
                                rows={2}
                                value={formData.address}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        address: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Payment Terms"
                                value={formData.payment_terms}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        payment_terms: e.target.value,
                                    })
                                }
                                placeholder="e.g., Net 30 days"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsCreateDialogOpen(false);
                            resetForm();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleCreateSupplier} variant="contained">
                        Create Supplier
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Edit Supplier</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Supplier Name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Contact Person (Optional)"
                                value={formData.contact_person}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        contact_person: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Email (Optional)"
                                type="email"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        email: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                            <TextField
                                fullWidth
                                label="Phone (Optional)"
                                value={formData.phone}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        phone: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Address (Optional)"
                                multiline
                                rows={2}
                                value={formData.address}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        address: e.target.value,
                                    })
                                }
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Payment Terms"
                                value={formData.payment_terms}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        payment_terms: e.target.value,
                                    })
                                }
                                placeholder="e.g., Net 30 days"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsEditDialogOpen(false);
                            resetForm();
                            setSelectedSupplier(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleUpdateSupplier} variant="contained">
                        Update Supplier
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Supplier</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "
                        {selectedSupplier?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsDeleteDialogOpen(false);
                            setSelectedSupplier(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSupplier}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SupplierList;
